import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import Text from '@ingka/text';
import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import { useLocale, useStores } from '@lotta/self-service/lottery-fe/feature-hooks';
import { LotteryConfigOverview } from '@lotta/self-service/lottery-fe/feature-lottery-config-overview';
import { getStoreIdNameDict } from '@lotta/shared/data-access-stores';
import type { LotteryConfig, TranslationConfig } from '@lotta/shared/types-lottery-schemas';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { LotteryFormContext } from '../lottery-form-context';
import type { LotteryConfigData, LotteryFormAPIErrorType } from '../types';
import { ConfirmLotteryError } from './ConfirmLotteryError';
import styles from './ReviewConfiguration.module.scss';

export type ReviewErrorTranslationKeys = {
  titleTranslationKey: string;
  bodyTranslationKey: string;
};

export type ReviewConfigurationProps = {
  lotteryConfigData: LotteryConfigData;
  onFormSubmit: (formData: { lottery: LotteryConfig; translation: TranslationConfig }) => void;
  onSecondaryAction: () => void;
  error?: LotteryFormAPIErrorType; // setting this will display the error message
  wrapFormTitle?: (title: string) => React.ReactNode;
};

export const ReviewConfiguration: React.FC<ReviewConfigurationProps> = ({
  lotteryConfigData,
  onFormSubmit,
  onSecondaryAction,
  error,
  wrapFormTitle = (title: string) => title,
}) => {
  const { result } = useContext(LotteryFormContext);
  const { t } = useTranslation();
  const { retailUnit, language } = useLocale();
  const { data: retailUnitData, error: retailUnitError } = useGetRetailUnitQuery({ retailUnit });
  const { data: storesData, error: storesError } = useStores({
    retailUnit,
    language,
    retailUnitData,
  });

  if (retailUnitError || storesError) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }

  const onSubmit = () => {
    onFormSubmit(lotteryConfigData);
  };
  const onBack = () => {
    onSecondaryAction();
    if (error) error.resetAPIError();
  };

  const storesDictionary = storesData ? getStoreIdNameDict(storesData['stores']) : {};

  return (
    <>
      {wrapFormTitle(t('general-review-configurations'))}
      <div className={styles['container']}>
        <InlineMessage
          className={styles['inline-message']}
          variant="informative"
          title={`${t('form-explanation-message-review-title')}`}
          body={`${t('form-explanation-message-review-body')}`}
        />
        <LotteryConfigOverview
          values={lotteryConfigData}
          currency={retailUnitData?.retailUnit.currency || ''}
          storesDictionary={storesDictionary}
          timezone={retailUnitData?.retailUnit.timeZone || ''}
        />
        <div className={styles['button-container']}>
          {error && <ConfirmLotteryError {...error.reviewErrorTranslationKeys} />}
          <Button
            className={styles['button-confirm']}
            text={`${t('general-review-confirm-lottery')}`}
            type="primary"
            onClick={onSubmit}
            data-testid="button-confirm"
            disabled={result?.isUpdateLotteryLoading || result?.isCreateLotteryLoading}
            loading={result?.isUpdateLotteryLoading || result?.isCreateLotteryLoading}
          />
          <Button
            className={styles['button-back']}
            text={`${t('general-back')}`}
            onClick={onBack}
          />
        </div>
      </div>
    </>
  );
};
